// src/components/DrawerCard.tsx
import React, { cloneElement } from 'react';
import {
  Card,
  CardActionArea,
  CardContent,
  Typography,
  Box,
  Tooltip,
  useTheme,
  type SxProps,
  type Theme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';

interface DrawerCardProps {
  label: string;
  icon: React.ReactNode;
  enabled: boolean;
  onClick: () => void;
  variant?: 'filled' | 'outlined';
  subtitle?: string;
  disabledTooltip?: string;
  sx?: SxProps<Theme>;
}

export const DrawerCard: React.FC<DrawerCardProps> = ({
  label,
  icon,
  enabled,
  onClick,
  variant = 'outlined',
  subtitle,
  disabledTooltip,
  sx,
}) => {
  const theme = useTheme();

  const primary = theme.palette.primary.main;
  const divider = theme.palette.divider;

  const iconBg =
    variant === 'filled' && enabled
      ? primary
      : alpha(theme.palette.background.paper, 1);

  const iconBorder =
    !enabled
      ? 'none'
      : variant === 'outlined'
        ? `1px solid ${alpha(primary, 0.6)}`
        : 'none';


  const iconColor =
    variant === 'filled' && enabled
      ? theme.palette.common.white
      : enabled
        ? primary
        : theme.palette.text.disabled;

  const labelColor = enabled ? theme.palette.text.primary : theme.palette.text.disabled;

  const content = (
    <Card
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: `1px solid ${alpha(divider, 0.9)}`,
        backgroundColor: alpha(theme.palette.primary.light, 0.02),
        transition: 'transform .15s ease, box-shadow .15s ease, border-color .15s ease, opacity .2s ease',
        opacity: enabled ? 1 : 0.55,
        ...(enabled && {
          '&:hover': {
            transform: 'translateY(-2px)',
            borderColor: alpha(primary, 0.3),
            boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
          },
        }),
        ...sx,
      }}
    >
      <CardActionArea
        disabled={!enabled}
        onClick={onClick}
        sx={{
          height: '100%',
          '& .MuiCardActionArea-focusHighlight': {
            opacity: 0.06,
          },
          cursor: enabled ? 'pointer' : 'not-allowed',
        }}
        aria-disabled={!enabled}
      >
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: { xs: 1.5, sm: 2, md: 3 },
            aspectRatio: '1 / 1',
          }}
        >
          <Box
            sx={{
              mb: { xs: 1, sm: 1.5, md: 2 },
              width: { xs: 48, sm: 64, md: 80 },
              height: { xs: 48, sm: 64, md: 80 },
              borderRadius: '50%',
              bgcolor: iconBg,
              border: iconBorder,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {cloneElement(icon as React.ReactElement, {
              sx: {
                fontSize: { xs: 24, sm: 28, md: 32 },
                color: iconColor,
              },
            })}
          </Box>

          <Typography
            variant="subtitle2"
            sx={{
              color: labelColor,
              fontSize: { xs: '0.9rem', sm: '1rem' },
              textAlign: 'center',
              fontWeight: 700,
            }}
          >
            {label}
          </Typography>

          {subtitle && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.25, textAlign: 'center' }}
            >
              {subtitle}
            </Typography>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  );

  return !enabled && disabledTooltip ? (
    <Tooltip title={disabledTooltip} arrow enterTouchDelay={0}>
      <Box>{content}</Box>
    </Tooltip>
  ) : (
    content
  );
};

export default DrawerCard;
